import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Tag, Users, TicketCheck, QrCode, Loader2, Store, ArrowRight, BarChart3, Heart, Star, Euro, ChevronDown, Clock } from "lucide-react";
import { relativeDate, euro, discountLabel, timeLeft } from "@/lib/format";
import InboxBell from "@/components/InboxBell";
import StoreSuggestionBanner from "@/components/StoreSuggestionBanner";
import { StatTile } from "@/components/StatTile";

export default function Dashboard() {
  const navigate = useNavigate();
  const { vendor } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [redemptions, setRedemptions] = useState([]);
  const [badges, setBadges] = useState({ reviews: 0, followers: 0, coupons: 0 });
  const [loading, setLoading] = useState(true);
  const [showRedemptions, setShowRedemptions] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/vendor/analytics", { headers: authHeaders("vendor") }),
      api.get("/vendor/redemptions", { headers: authHeaders("vendor") }),
      api.get("/vendor/badges", { headers: authHeaders("vendor") }),
    ]).then(([a, r, b]) => {
      setAnalytics(a.data);
      setRedemptions((r.data.redemptions || []).slice(0, 8));
      setBadges(b.data || {});
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;

  const storeReady = !!vendor?.store_name;
  const activeOffers = analytics?.active_offers || [];

  const stats = [
    { label: "Follower", value: analytics?.followers ?? 0, icon: Heart, color: "text-pink-600 bg-pink-50", to: "/vendor/followers", badge: badges.followers || 0 },
    { label: "Offerte attive", value: analytics?.live_offers ?? 0, icon: Tag, color: "text-[#E63946] bg-[#FFE4E6]", to: "/vendor/offers" },
    { label: "Coupon richiesti", value: analytics?.total_claims ?? 0, icon: TicketCheck, color: "text-blue-600 bg-blue-50", to: "/vendor/coupon-clients", badge: badges.coupons || 0 },
    { label: "Riscattati", value: analytics?.redeemed ?? 0, icon: Users, color: "text-emerald-600 bg-emerald-50", to: "/vendor/history" },
    { label: "Recensioni", value: analytics?.reviews_count ?? 0, icon: Star, color: "text-amber-600 bg-amber-50", to: "/vendor/reviews", badge: badges.reviews || 0 },
  ];

  return (
    <div className="space-y-6 kn-fade-up">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900">Ciao, {vendor?.store_name || "commerciante"} 👋</h1>
          <p className="text-slate-500">Ecco come vanno le tue offerte oggi.</p>
        </div>
        <div className="flex items-center gap-2">
        <InboxBell role="vendor" />
        <Button onClick={() => navigate("/vendor/stats")} variant="outline" className="hidden sm:flex h-11 rounded-xl font-bold gap-2" data-testid="dash-stats">
          <BarChart3 className="h-4 w-4" /> Statistiche
        </Button>
        <Button onClick={() => navigate("/vendor/validate")} variant="outline" className="hidden sm:flex h-11 rounded-xl font-bold gap-2" data-testid="dash-validate">
          <QrCode className="h-4 w-4" /> Valida in cassa
        </Button>
        <Button onClick={() => window.dispatchEvent(new Event("kn-replay-tutorial-vendor"))} variant="ghost" className="h-11 rounded-xl font-semibold text-slate-500" data-testid="replay-tutorial-vendor">
          Rivedi tutorial
        </Button>
        </div>
      </div>

      {!storeReady && (
        <button onClick={() => navigate("/vendor/store")} data-testid="setup-store-banner" className="w-full flex items-center gap-3 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-left">
          <Store className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="font-bold text-amber-800">Completa il profilo del negozio</p>
            <p className="text-amber-700 text-sm">Aggiungi posizione, logo e orari per pubblicare offerte.</p>
          </div>
          <ArrowRight className="h-5 w-5 text-amber-600" />
        </button>
      )}

      {storeReady && <StoreSuggestionBanner vendor={vendor} />}

      {/* Stats grid — sempre in alto */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
        {stats.map((s) => (
          <StatTile key={s.label} testId={`stat-${s.label}`} icon={s.icon} value={s.value} label={s.label}
            accent={s.color.split(" ")[0]} badge={s.badge || 0} onClick={() => navigate(s.to)} />
        ))}
      </div>

      {/* Incassi — card dedicata in evidenza */}
      <button onClick={() => navigate("/vendor/stats")} data-testid="revenue-card" className="w-full rounded-2xl p-6 text-left kn-shadow-soft flex items-center gap-4 bg-gradient-to-br from-emerald-600 to-emerald-500 text-white hover:brightness-105 transition">
        <div className="h-14 w-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><Euro className="h-8 w-8" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-white/85 text-sm font-semibold">Incassi totali confermati</p>
          <p className="text-3xl sm:text-4xl font-extrabold tabular-nums" data-testid="revenue-value">{euro(analytics?.revenue ?? 0)}</p>
        </div>
        <ArrowRight className="h-6 w-6 shrink-0" />
      </button>

      {/* Big publish CTA — stile giallo Amazon */}
      <button onClick={() => navigate("/vendor/offers?new=1")} data-testid="publish-cta" className="w-full rounded-2xl p-6 text-slate-900 text-left kn-shadow-soft flex items-center gap-4 bg-[#FFD814] hover:bg-[#F7CA00] transition">
        <div className="h-14 w-14 rounded-2xl bg-slate-900/10 flex items-center justify-center shrink-0"><Plus className="h-8 w-8" /></div>
        <div className="flex-1">
          <p className="text-xl font-extrabold">Pubblica un'offerta</p>
          <p className="text-slate-700 text-sm">Sconto %, importo fisso, prezzo speciale o 2x1 — in 30 secondi.</p>
        </div>
        <ArrowRight className="h-6 w-6" />
      </button>

      {/* Valida coupon Cliente — sotto Pubblica offerta */}
      <button onClick={() => navigate("/vendor/validate")} data-testid="validate-cta" className="w-full rounded-2xl p-5 text-left kn-shadow-soft flex items-center gap-4 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 transition">
        <div className="h-12 w-12 rounded-2xl bg-slate-900 flex items-center justify-center shrink-0"><QrCode className="h-7 w-7 text-white" /></div>
        <div className="flex-1">
          <p className="text-lg font-extrabold">Valida coupon Cliente</p>
          <p className="text-slate-500 text-sm">Scansiona o inserisci il codice per confermare lo sconto in cassa.</p>
        </div>
        <ArrowRight className="h-6 w-6" />
      </button>

      {/* Offerte attive adesso */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-extrabold text-lg text-slate-900">Offerte attive adesso</h2>
          <button onClick={() => navigate("/vendor/offers")} className="text-sm font-bold text-[#E63946]" data-testid="active-offers-see-all">Gestisci</button>
        </div>
        {activeOffers.length === 0 ? (
          <div className="kn-card p-8 text-center text-slate-400">Nessuna offerta attiva. Pubblicane una!</div>
        ) : (
          <div className="space-y-2" data-testid="active-offers-list">
            {activeOffers.map((o) => (
              <button key={o.offer_id} onClick={() => navigate("/vendor/offers")} className="w-full kn-card p-3.5 flex items-center gap-3 text-left hover:bg-slate-50 transition" data-testid={`active-offer-${o.offer_id}`}>
                <div className="h-11 w-11 rounded-xl kn-gradient flex items-center justify-center shrink-0 text-white font-extrabold text-sm">{discountLabel(o)}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">{o.title}</p>
                  <p className="text-xs text-slate-400 flex items-center gap-1.5 truncate">
                    <Clock className="h-3 w-3 shrink-0" /> {timeLeft(o.expires_at)} · {o.claims} coupon richiesti
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-300 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Recent redemptions — comprimibile */}
      <div>
        <button
          onClick={() => setShowRedemptions((v) => !v)}
          className="w-full flex items-center justify-between mb-3"
          data-testid="toggle-redemptions"
        >
          <h2 className="font-extrabold text-lg text-slate-900 flex items-center gap-2">
            Ultimi riscatti
            {redemptions.length > 0 && <span className="text-sm font-bold text-slate-400">({redemptions.length})</span>}
          </h2>
          <ChevronDown className={`h-5 w-5 text-slate-500 transition-transform ${showRedemptions ? "rotate-180" : ""}`} />
        </button>
        {showRedemptions && (
          redemptions.length === 0 ? (
            <div className="kn-card p-8 text-center text-slate-400">Nessun coupon riscattato ancora.</div>
          ) : (
            <div className="space-y-2" data-testid="redemptions-list">
              <div className="flex justify-end">
                <button onClick={() => navigate("/vendor/history")} className="text-sm font-bold text-[#E63946]">Vedi tutto</button>
              </div>
              {redemptions.map((r, i) => (
                <div key={i} className="kn-card p-3.5 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><TicketCheck className="h-5 w-5 text-emerald-600" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 truncate">{r.title}</p>
                    <p className="text-xs text-slate-400 truncate">{r.client_name} · {relativeDate(r.redeemed_at)}</p>
                  </div>
                  <span className="text-xs font-bold text-slate-400 uppercase">{r.online ? "Online" : "In cassa"}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
